// ABOUTME: Canonical Zod schemas and TypeScript types for the RAAS (Runway Awareness and Advisory System) API.
import { z } from 'zod';

// =====================================
// AUTHENTICATION SCHEMAS
// =====================================

export const ZRaasAuthTokenRequest = z.object({
  companyName: z.string().min(1),
  apiKey: z.string().min(1),
  apiSecret: z.string().min(1),
});

export const ZRaasAuthTokenResponse = z.object({
  token: z.string().nullable(),
  refreshToken: z.string().nullable(),
  expiresOnUTC: z.string(),
});

// =====================================
// COMMON RESPONSE WRAPPER
// =====================================

export const ZRaasWebApiMessage = z.object({
  text: z.string().nullable(),
});

export const ZRaasWebApiResponseBase = z.object({
  version: z.string().nullable(),
  requestId: z.string().nullable(),
  messages: z.array(ZRaasWebApiMessage).nullable(),
});

// =====================================
// NEXT DUE SCHEMAS
// =====================================

export const ZRaasTrackingPart = z.object({
  partID: z.number().int(),
  serialNumber: z.string().nullable(),
  partNumber: z.string().nullable(),
  partDescription: z.string().nullable(),
  position: z.string().nullable(),
  ata: z.string().nullable(),
});

export const ZRaasPlanningCategory = z.object({
  planningCategoryID: z.number().int(),
  description: z.string().nullable(),
  value: z.number().nullable(),
});

export const ZRaasJobCard = z.object({
  jobCardID: z.number().int(),
  number: z.string().nullable(),
  status: z.string().nullable(),
  groupName: z.string().nullable(),
  ata: z.string().nullable(),
  details: z.string().nullable(),
  notes: z.string().nullable(),
  openedDateUTC: z.string().nullable(),
  closedDateUTC: z.string().nullable(),
  plannedDate: z.string().nullable(),
  priority: z.string().nullable(),
  period: z.string().nullable(),
  isAirworthinessItem: z.boolean().nullable(),
  dmiNumber: z.string().nullable(),
  deferredIAW: z.string().nullable(),
  inExtension: z.boolean().nullable(),
});

export const ZRaasHoursLimit = z.object({
  interval: z.number().nullable(),
  nextDue: z.number().nullable(),
  remaining: z.number().nullable(),
});

export const ZRaasCyclesLimit = z.object({
  interval: z.number().nullable(),
  nextDue: z.number().nullable(),
  remaining: z.number().nullable(),
  units: z.string().nullable(),
});

export const ZRaasCalendarLimit = z.object({
  interval: z.number().nullable(),
  nextDue: z.number().nullable(),
  remaining: z.number().nullable(),
  units: z.string().nullable(),
  remainingDays: z.number().nullable(),
  nextDueUTC: z.string().nullable(),
});

export const ZRaasNextDueItem = z.object({
  constraintType: z.string().nullable(),
  constraintDescription: z.string().nullable(),
  useWIL: z.boolean().nullable(),
  trackingPart: ZRaasTrackingPart,
  sttPart: z.unknown().nullable(),
  planningCategory: ZRaasPlanningCategory.nullable(),
  jobCard: ZRaasJobCard.nullable(),
  hoursLimit: ZRaasHoursLimit.nullable(),
  cyclesLimit: ZRaasCyclesLimit.nullable(),
  calendarLimit: ZRaasCalendarLimit.nullable(),
});

export const ZRaasNextDueAircraft = z.object({
  serialNumber: z.string().nullable(),
  resultType: z.string().nullable(),
  partNumber: z.string().nullable(),
  partDescription: z.string().nullable(),
  registration: z.string().nullable(),
  model: z.string().nullable(),
  variant: z.string().nullable(),
  series: z.string().nullable(),
  operator: z.string().nullable(),
  base: z.string().nullable(),
  status: z.string().nullable(),
});

export const ZRaasNextDueResponse = z.object({
  aircraft: ZRaasNextDueAircraft.nullable(),
  items: z.array(ZRaasNextDueItem).nullable(),
  totalRecordCount: z.number().int().nullable(),
});

// =====================================
// FLIGHT LOG SCHEMAS
// =====================================

export const ZRaasFlightLogFluidUnit = z.enum([
  'None',
  'Gallons',
  'Liters',
  'Quarts',
]);

export const ZRaasFlightLogFluid = z
  .object({
    volume: z.number(),
    unit: ZRaasFlightLogFluidUnit,
  })
  .strict();

export const ZRaasFlightLogAccessoryType = z.enum(['APU', 'Engine']);

export const ZRaasFlightLogAccessory = z
  .object({
    type: ZRaasFlightLogAccessoryType,
    position: z.string().nullable().optional(),
    minutes: z.number().nullable().optional(),
    cycles: z.number().nullable().optional(),
    fluid: ZRaasFlightLogFluid.optional(),
  })
  .strict();

export const ZRaasFlightLogCreateRequest = z
  .object({
    flightId: z.string().nullable().optional(),
    aircraftPartID: z.number().int().nullable().optional(),
    serialNumber: z.string().min(1).nullable().optional(),
    registrationNumber: z.string().min(1).nullable().optional(),
    logNumber: z.string().min(1).nullable().optional(),
    leg: z.number().int().optional(),
    flightNumber: z.string().min(1),
    origin: z.string().min(1),
    destination: z.string().min(1),
    liftOffUTC: z.string().min(1),
    touchdownUTC: z.string().min(1),
    movementNumber: z.number().int().optional(),
    minutesInAir: z.number(),
    cycles: z.number().nullable().optional(),
    creatingSystemName: z.string().min(1).nullable().optional(),
    fuelLift: ZRaasFlightLogFluid.optional(),
    accessories: z.array(ZRaasFlightLogAccessory).min(1).optional(),
  })
  .strict()
  .refine(
    (data) =>
      [
        data.aircraftPartID != null,
        data.serialNumber != null,
        data.registrationNumber != null,
      ].filter(Boolean).length === 1,
    {
      message:
        'Exactly one of aircraftPartID, serialNumber, or registrationNumber is required.',
    },
  );

export const ZRaasFlightLog = z.object({
  flightLogID: z.number().int(),
  aircraftPartID: z.number().int().nullable(),
  logNumber: z.string().nullable(),
  leg: z.string().nullable(),
  flightNumber: z.string().nullable(),
  origin: z.string().nullable(),
  destination: z.string().nullable(),
  liftOffUTC: z.string().nullable(),
  touchdownUTC: z.string().nullable(),
  movementNumber: z.number().int().nullable(),
  creatingSystemName: z.string().nullable(),
  cycles: z.number().nullable().optional(),
});

export const ZRaasFlightLogSuccessResponse = ZRaasWebApiResponseBase.extend({
  data: ZRaasFlightLog,
});

export const ZRaasFlightLogErrorResponse = ZRaasWebApiResponseBase.extend({
  data: z.null(),
});

// =====================================
// MAINTENANCE RELEASE SCHEMAS
// =====================================

export const ZRaasMaintenanceReleaseItem = z.object({
  itemDateUTC: z.string().nullable(),
  enteredBy: z.string().nullable(),
  hsn: z.number().nullable(),
  lsn: z.number().nullable(),
  jobCardNumber: z.string().nullable(),
  jobCardGroup: z.string().nullable(),
  jobCardStatus: z.string().nullable(),
  jobCardATA: z.string().nullable(),
  jobCardDetails: z.string().nullable(),
  jobCardOpenedDateUTC: z.string().nullable(),
  jobCardClosedDateUTC: z.string().nullable(),
  discrepancyNumber: z.string().nullable(),
  discrepancyReportedDateUTC: z.string().nullable(),
  discrepancyStatus: z.string().nullable(),
  discrepancyATA: z.string().nullable(),
  discrepancyDescription: z.string().nullable(),
  discrepancyTitle: z.string().nullable(),
  rectificationDateUTC: z.string().nullable(),
  rectificationCloseJobCard: z.boolean().nullable(),
  rectificationDetails: z.string().nullable(),
});

export const ZRaasMaintenanceRelease = z.object({
  id: z.number().int(),
  releaseName: z.string().nullable(),
  releaseDateUTC: z.string().nullable(),
  releaseHSN: z.number().nullable(),
  releaseLSN: z.number().nullable(),
  creationText: z.string().nullable(),
  acceptanceText: z.string().nullable(),
  lastAcceptanceUTC: z.string().nullable(),
  acceptanceName: z.string().nullable(),
  serialNumber: z.string().nullable(),
  partNumber: z.string().nullable(),
  partDescription: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  items: z.array(ZRaasMaintenanceReleaseItem).nullable(),
});

// =====================================
// AIRCRAFT SCHEMAS
// =====================================

export const ZRaasAircraft = z.object({
  serialNumber: z.string().nullable(),
  partNumber: z.string().nullable(),
  partDescription: z.string().nullable(),
  registration: z.string().nullable(),
  model: z.string().nullable(),
  variant: z.string().nullable(),
  series: z.string().nullable(),
  operator: z.string().nullable(),
  base: z.string().nullable(),
  status: z.string().nullable(),
});

export const ZRaasAccessorySTT = z.object({
  partDescription: z.string().nullable().optional(),
  partNumber: z.string().nullable().optional(),
  serialNumber: z.string().nullable().optional(),
  position: z.string().nullable().optional(),
  inheritsTime: z.boolean().nullable().optional(),
  mapTypeAPI: z.string().nullable().optional(),
  // RAAS-authoritative position ordinal scoped per mapTypeAPI (Engine 1/2/3/4,
  // APU 1). It is the value FlightLogs expects as accessories[].position, and is
  // unique only together with mapTypeAPI (an APU and an engine can both be 1).
  // 0 with an empty mapTypeAPI marks an accessory RAAS does not expose to the API.
  mapPositionAPI: z.number().int().nullable().optional(),
  label: z.string().nullable().optional(),
});

export const ZRaasInputField = z.object({
  field: z.string().nullable().optional(),
  decimals: z.number().int().nullable().optional(),
  defaultValue: z.string().nullable().optional(),
  formulaText: z.string().nullable().optional(),
});

export const ZRaasAircraftStatus = z.object({
  serialNumber: z.string().nullable().optional(),
  resultType: z.string().nullable().optional(),
  partNumber: z.string().nullable().optional(),
  partDescription: z.string().nullable().optional(),
  registrationNumber: z.string().nullable().optional(),
  serviceable: z.boolean().nullable().optional(),
  hsn: z.number().nullable().optional(),
  csn: z.number().nullable().optional(),
  asOfUTC: z.string().nullable().optional(),
  lastFlightFlownUTC: z.string().nullable().optional(),
  parked: z.boolean().nullable().optional(),
  serviceableReason: z.string().nullable().optional(),
  unserviceableReason: z.string().nullable().optional(),
  currentLocation: z.string().nullable().optional(),
  homeBase: z.string().nullable().optional(),
  dispatchHours: z.number().nullable().optional(),
  dispatchDays: z.number().nullable().optional(),
  dispatchCycles: z.number().nullable().optional(),
  utilizationHours: z.number().nullable().optional(),
  utilizationCycles: z.number().nullable().optional(),
  utilizationPerUnits: z.string().nullable().optional(),
  accessorySTT: z.array(ZRaasAccessorySTT).nullable().optional(),
  inputFields: z.array(ZRaasInputField).nullable().optional(),
});

export const ZRaasAircraftStatusResponse = ZRaasWebApiResponseBase.extend({
  data: ZRaasAircraftStatus,
});

// =====================================
// CREDENTIAL CONFIG SCHEMA
// =====================================

export const ZRaasCredentialConfig = z.object({
  id: z.string(),
  integration: z.literal('RAAS'),
  operatorId: z.string(),
  company_name: z.string(),
  api_key: z.string(),
  api_secret: z.string(),
  token: z.string().optional(),
  tokenExpiry: z.string().optional(),
});

// =====================================
// TYPE EXPORTS (canonical types via z.infer)
// =====================================

export type RaasAuthTokenRequest = z.infer<typeof ZRaasAuthTokenRequest>;
export type RaasAuthTokenResponse = z.infer<typeof ZRaasAuthTokenResponse>;
export type RaasWebApiMessage = z.infer<typeof ZRaasWebApiMessage>;
export type RaasTrackingPart = z.infer<typeof ZRaasTrackingPart>;
export type RaasPlanningCategory = z.infer<typeof ZRaasPlanningCategory>;
export type RaasJobCard = z.infer<typeof ZRaasJobCard>;
export type RaasHoursLimit = z.infer<typeof ZRaasHoursLimit>;
export type RaasCyclesLimit = z.infer<typeof ZRaasCyclesLimit>;
export type RaasCalendarLimit = z.infer<typeof ZRaasCalendarLimit>;
export type RaasNextDueItem = z.infer<typeof ZRaasNextDueItem>;
export type RaasNextDueAircraft = z.infer<typeof ZRaasNextDueAircraft>;
export type RaasNextDueResponse = z.infer<typeof ZRaasNextDueResponse>;
export type RaasFlightLogFluidUnit = z.infer<typeof ZRaasFlightLogFluidUnit>;
export type RaasFlightLogFluid = z.infer<typeof ZRaasFlightLogFluid>;
export type RaasFlightLogAccessoryType = z.infer<
  typeof ZRaasFlightLogAccessoryType
>;
export type RaasFlightLogAccessory = z.infer<typeof ZRaasFlightLogAccessory>;
export type RaasFlightLogCreateRequest = z.infer<
  typeof ZRaasFlightLogCreateRequest
>;
export type RaasFlightLog = z.infer<typeof ZRaasFlightLog>;
export type RaasFlightLogSuccessResponse = z.infer<
  typeof ZRaasFlightLogSuccessResponse
>;
export type RaasFlightLogErrorResponse = z.infer<
  typeof ZRaasFlightLogErrorResponse
>;
export type RaasMaintenanceReleaseItem = z.infer<
  typeof ZRaasMaintenanceReleaseItem
>;
export type RaasMaintenanceRelease = z.infer<typeof ZRaasMaintenanceRelease>;
export type RaasAircraft = z.infer<typeof ZRaasAircraft>;
export type RaasAccessorySTT = z.infer<typeof ZRaasAccessorySTT>;
export type RaasInputField = z.infer<typeof ZRaasInputField>;
export type RaasAircraftStatus = z.infer<typeof ZRaasAircraftStatus>;
export type RaasAircraftStatusResponse = z.infer<
  typeof ZRaasAircraftStatusResponse
>;
export type RaasCredentialConfig = z.infer<typeof ZRaasCredentialConfig>;

// Legacy type aliases for backward compatibility with existing callers
export type NextDue = RaasNextDueItem;
export type RAASFlightLogDataInput = RaasFlightLogCreateRequest;
